import { LayaGL } from "../../layagl/LayaGL";
import { Rectangle } from "../../maths/Rectangle";
import { Vector4 } from "../../maths/Vector4";
import { DrawType } from "../../RenderEngine/RenderEnum/DrawType";
import { IndexFormat } from "../../RenderEngine/RenderEnum/IndexFormat";
import { MeshTopology } from "../../RenderEngine/RenderEnum/RenderPologyMode";
import { Pool } from "../../utils/Pool";
import { FastSinglelist } from "../../utils/SingletonList";
import { BlendModeHandler } from "../../webgl/canvas/BlendMode";
import { Shader2D } from "../../webgl/shader/d2/Shader2D";
import { GraphicsShaderInfo } from "../../webgl/shader/d2/value/GraphicsShaderInfo";
import { SubmitBase } from "../../webgl/submit/SubmitBase";
import { Render2DProcessor } from "../Render2DProcessor";
import { BaseRender2DType } from "../SpriteConst";
export class GraphicsRenderData {
    constructor(owner) {
        this._renderElements = [];
        this._submits = new FastSinglelist;
        this._bufferBlocks = [];
        this.owner = owner;
    }
    clear() {
        let len = this._submits.length;
        let i = 0;
        for (i = 0; i < len; i++) {
            this._submits.elements[i].clear();
        }
        this._bufferBlocks.length = 0;
        this._submits.length = 0;
    }
    destroy() {
        this.clear();
        let material = this.owner.material;
        let elements = this._renderElements;
        for (let i = 0; i < elements.length; i++) {
            if (material) {
                material._removeOwnerElement(elements[i]);
            }
            GraphicsRenderData._pool.recover(elements[i]);
        }
        elements.length = 0;
        let submits = this._submits.elements;
        for (let i = 0; i < this._submits.length; i++) {
            submits[i].destroy();
        }
        this._submits.destroy();
        this._submits = null;
        this.owner = null;
    }
    updateRenderElement(graphics, struct, handle) {
        let originLen = this._renderElements.length;
        let submits = this._submits;
        let submitLength = submits.length;
        let needUpdate = originLen !== submitLength;
        let flength = Math.max(originLen, submitLength);
        let blocks = this._bufferBlocks;
        for (let i = 0; i < flength; i++) {
            let submit = submits.elements[i];
            let element = this._renderElements[i];
            if (i < submitLength) {
                if (!element) {
                    element = GraphicsRenderData._pool.take();
                    element.value2DShaderData = struct.spriteShaderData;
                    element.owner = struct;
                    this._renderElements[i] = element;
                }
                element.primitiveShaderData = submit._internalInfo.shaderData;
                element.renderStateIsBySprite = submit.renderStateIsBySprite && graphics._useSpriteState;
                if (submit.material) {
                    element.subShader = submit.material.shader.getSubShaderAt(0);
                    element.materialShaderData = submit.material.shaderData;
                    submit.material._setOwner2DElement(element);
                }
                else {
                    element.subShader = Shader2D.graphicsShader.getSubShaderAt(0);
                }
                let geometry = element.geometry;
                geometry.bufferState = submit.mesh.bufferState;
                geometry.clearRenderParams();
                let indexView = this._updateIndexViews(submit, geometry);
                let vertexBuffer = submit.mesh._buffer.vertexBuffer;
                {
                    let vertexBlock = LayaGL.render2DRenderPassFactory.createGraphic2DBufferBlock();
                    vertexBlock.vertexs = submit.vertexs;
                    vertexBlock.indexView = indexView;
                    vertexBlock.vertexBuffer = vertexBuffer;
                    blocks.push(vertexBlock);
                }
                this._updateGraphicsKeys(element, submit);
            }
            else {
                graphics.material && (graphics.material._removeOwnerElement(element));
                GraphicsRenderData._pool.recover(element);
            }
        }
        this._renderElements.length = submitLength;
        if (needUpdate) {
            struct.renderElements = this._renderElements;
        }
        handle.applyVertexBufferBlock(blocks);
    }
    _updateIndexViews(submit, geometry) {
        let indexView = submit.mesh.checkIndex(submit.indexCount);
        indexView.setGeometry(geometry);
        submit.indexView = indexView;
        indexView.setData(submit.indices);
        submit.indexCount = 0;
        submit.indices.length = 0;
        return indexView;
    }
    _updateGraphicsKeys(element, submit) {
        let useCustomMaterial = submit.material ? 1 : 0;
        let mc = (useCustomMaterial === 0 && submit._internalInfo.materialClip) ? 1 : 0;
        let texture;
        let textureHost = submit._internalInfo.textureHost;
        if (textureHost)
            texture = textureHost.bitmap || textureHost;
        element.type = submit._key.blendShader
            | (useCustomMaterial << 4)
            | (mc << 5)
            | ((texture ? texture.id : 0) << 6);
    }
    setRenderElement(struct, handle) {
        struct.renderElements = this._renderElements;
        handle.applyVertexBufferBlock(this._bufferBlocks);
    }
    createSubmit(runner, mesh, material) {
        let elements = this._submits.elements;
        let submit = null;
        if (elements.length > this._submits.length) {
            submit = elements[this._submits.length];
            submit.update(runner, mesh, material);
            this._submits.length++;
        }
        else {
            submit = SubmitBase.create(runner, mesh, material);
            this._submits.add(submit);
        }
        return submit;
    }
    touchRes(res) {
    }
}
GraphicsRenderData._pool = Pool.createPool2(() => {
    let element = LayaGL.render2DRenderPassFactory.createPrimitiveRenderElement2D();
    element.renderStateIsBySprite = false;
    element.nodeCommonMap = ["Sprite2D"];
    return element;
}, (element, needGeometry) => {
    if (needGeometry || needGeometry == null) {
        element.geometry = LayaGL.renderDeviceFactory.createRenderGeometryElement(MeshTopology.Triangles, DrawType.DrawElement);
        element.geometry.indexFormat = IndexFormat.UInt16;
    }
    else {
        if (element.geometry) {
            element.geometry.destroy();
            element.geometry = null;
        }
    }
}, (element) => {
    if (element.geometry) {
        element.geometry.clearRenderParams();
        element.geometry.bufferState = null;
    }
    element.materialShaderData = null;
    element.value2DShaderData = null;
    element.primitiveShaderData = null;
    element.globalShaderData = null;
    element.owner = null;
    element.subShader = null;
    element.renderStateIsBySprite = false;
    element.type = 0;
});
export class SubStructRender {
    constructor() {
        this._renderElement = null;
        this._shaderData = null;
        this._handle = null;
        this._submit = null;
        this._internalInfo = null;
        this._rtRect = new Rectangle();
        this._shaderData = LayaGL.renderDeviceFactory.createShaderData();
        this._handle = LayaGL.render2DRenderPassFactory.create2D2DPrimitiveDataHandle();
        this._submit = new SubmitBase;
        this._internalInfo = new GraphicsShaderInfo();
        this._submit._internalInfo = this._internalInfo;
        this._renderElement = GraphicsRenderData._pool.take();
        this._renderElement.value2DShaderData = this._shaderData;
        this._renderElement.subShader = Shader2D.graphicsShader.getSubShaderAt(0);
        this._renderElement.primitiveShaderData = this._submit._internalInfo.shaderData;
        this._renderElement.nodeCommonMap = ["Sprite2D"];
        this._renderElement.geometry = Render2DProcessor.runner.inv_geometry;
        BlendModeHandler.initBlendMode(this._shaderData);
        this._internalInfo.enableVertexSize = true;
    }
    bind(sprite, subRenderPass, subStruct) {
        this._sprite = sprite;
        this._subRenderPass = subRenderPass;
        this._subStruct = subStruct;
        this._subStruct.spriteShaderData = this._shaderData;
        this._subStruct.renderType = BaseRender2DType.graphics;
        this._submit.material = sprite.material;
        subStruct.renderDataHandler = this._handle;
        subStruct.renderMatrix = sprite.globalTrans.getMatrix();
        subStruct.renderElements = [this._renderElement];
        this._renderElement.owner = this._subStruct;
        this._renderElement.type = this._subStruct.blendMode;
    }
    _updateRenderOffset(rect) {
        rect.cloneTo(this._rtRect);
        let originPass = this._subRenderPass;
        let matrix = originPass.offsetMatrix;
        if (this._sprite.mask) {
            let mask = this._sprite.mask;
            let transform = mask.transform;
            if (transform) {
                transform.cloneTo(matrix);
            }
            matrix.invert();
        }
        else {
            matrix.identity();
        }
        matrix.tx = matrix.a * rect.x + matrix.c * rect.y + matrix.tx;
        matrix.ty = matrix.b * rect.x + matrix.d * rect.y + matrix.ty;
        originPass.offsetMatrix = matrix;
    }
    updateQuat(oriRT, destRT) {
        var _a;
        this._handle.mask = (_a = this._sprite.mask) === null || _a === void 0 ? void 0 : _a._struct;
        if (this._submit._key.blendShader !== this._subStruct.blendMode) {
            this._submit._key.blendShader = this._subStruct.blendMode;
            BlendModeHandler.setShaderData(this._subStruct.blendMode, this._internalInfo.shaderData);
        }
        if (this._internalInfo.textureHost == destRT)
            return;
        if (destRT) {
            this._renderElement.type = destRT._id << 6;
        }
        else {
            this._renderElement.type = 0;
        }
        this._internalInfo.textureHost = destRT;
    }
    _updateVertexSize() {
        let destRT = this._internalInfo.textureHost;
        if (!destRT)
            return;
        let _rtRect = this._rtRect;
        let vSize = Vector4.TEMP;
        vSize.x = _rtRect.x;
        vSize.y = _rtRect.y;
        let width = destRT.sourceWidth;
        let height = destRT.sourceHeight;
        if (width > 0 && height > 0) {
            vSize.z = width;
            vSize.w = height;
            vSize.x -= (vSize.z - _rtRect.width) / 2;
            vSize.y -= (vSize.w - _rtRect.height) / 2;
        }
        else {
            vSize.z = _rtRect.width;
            vSize.w = _rtRect.height;
        }
        this._internalInfo.vertexSize = vSize;
    }
    destroy() {
        this._renderElement.geometry = null;
        GraphicsRenderData._pool.recover(this._renderElement);
        this._submit.destroy();
        this._submit = null;
        this._internalInfo = null;
        this._handle = null;
        this._subRenderPass = null;
        this._subStruct = null;
        this._sprite = null;
    }
}
